import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Chip, useMediaQuery } from '@mui/material'
import { Container, Content, Logo, Title } from './styles'
import { logEvent } from '../../firebase/firebase-config'
import { detectIos } from '../../utils'
import appLogo from '../../img/logo.png'
import {
  REACT_APP_HARDCODED_LOGO,
  REACT_APP_SHOW_STOP_PAGE_CTA_APP_BANNER,
  REACT_APP_ANDROID_PACKAGE_NAME,
  REACT_APP_IOS_BUNDLE_ID,
  REACT_APP_OG_IMAGE
} from '../../constants/config'
import { theme } from '../../theme/theme'

/** <StopPageAppBanner/>
 * @description The component is displayed on the stop page when REACT_APP_SHOW_STOP_PAGE_CTA_APP_BANNER is true.
 * Shows a banner to encourage users to download the app with device-specific app store links.
 *
 * Initial state:
 *     - Conditional rendering based on REACT_APP_SHOW_STOP_PAGE_CTA_APP_BANNER environment variable
 *
 * Methods:
 *     - handleOpenApp(): Detects the device platform and opens the appropriate app store
 *                        (Google Play for Android, App Store for iOS) in a new window.
 *                        Sends "stop_page_app_banner_cta" event to Analytics.
 * @returns {JSX.Element}
 * @constructor
 */

export const StopPageAppBanner = () => {
  const { t } = useTranslation()
  const mobile = useMediaQuery('(max-width:480px)')

  // Leer parámetros de la URL para determinar si se debe mostrar el banner
  const urlParams = new URLSearchParams(window.location.search)
  const showBannerParam = urlParams.get('showBanner')

  const user = useSelector(state => state?.user?.auth)
  const userPosition = useSelector(state => state?.user?.userPosition)
  const userData = useSelector(state => state?.user?.userData)
  const cityConfig = useSelector(state => state?.ui?.cityConfig)
  const stopSelected = useSelector(state => state?.ui?.stopSelected)

  /** handleOpenApp()
   * @description Detects the device platform and opens the appropriate app store
   * (Google Play for Android, App Store for iOS) in a new window.
   * Sends "stop_page_app_banner_cta" event to Analytics.
   * @returns {Promise<void>}
   */
  const handleOpenApp = async () => {
    try {
      const isIOS = detectIos()
      let appUrl = ''

      if (isIOS && REACT_APP_IOS_BUNDLE_ID) {
        // iOS App Store URL
        appUrl = `https://apps.apple.com/app/id${REACT_APP_IOS_BUNDLE_ID}`
      } else if (!isIOS && REACT_APP_ANDROID_PACKAGE_NAME) {
        // Android Play Store URL
        appUrl = `https://play.google.com/store/apps/details?id=${REACT_APP_ANDROID_PACKAGE_NAME}`
      }

      if (appUrl) {
        // Send analytics event
        logEvent('stop_page_app_banner_cta', {
          lat: userPosition?.lat || null,
          lng: userPosition?.lng || null,
          user_gender: userData?.gender || null,
          user_birthday_timestamp: userData?.birthday?.long_value || null,
          os: 'web',
          user_id: user?.uid || null,
          city_id: cityConfig?.city_id?.toString() || null,
          stop_id: stopSelected?.stop_id || null,
          stop_lat: stopSelected?.stop_lat || null,
          stop_lng: stopSelected?.stop_lon || null,
          stop_address: stopSelected?.stop_name || null,
          platform: isIOS ? 'ios' : 'android',
          url: appUrl
        })

        // Open app store in new window
        window.open(appUrl, '_blank')
      }
    } catch (e) {
      console.error('Error opening app store', e)
    }
  }

  // Don't render if the feature flag is disabled, no package names are configured, not on mobile, or showBanner parameter is not set to 'true'
  if (!REACT_APP_SHOW_STOP_PAGE_CTA_APP_BANNER || (!REACT_APP_ANDROID_PACKAGE_NAME && !REACT_APP_IOS_BUNDLE_ID) || !mobile || showBannerParam !== 'true') {
    return null
  }

  return (
    <Container>
      <Logo
        alt='App logo'
        src={REACT_APP_OG_IMAGE || REACT_APP_HARDCODED_LOGO || appLogo}
      />
      <Content>
        <Title>{t('smart_banners.top.title')}</Title>
        <Chip
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            fontSize: 14,
            padding: '0 2px',
            '&:hover': {
              backgroundColor: theme.palette.primary.main
            },
            '&:focus': {
              backgroundColor: theme.palette.primary.main
            },
            '&:active': {
              backgroundColor: theme.palette.primary.main
            }
          }}
          label={t('smart_banners.top.open')}
          onClick={handleOpenApp}
          clickable
        />
      </Content>
    </Container>
  )
}
